import { useMemo, useRef, useState } from 'react';
import '../Styles/Delegate.scss';
import {
    Stack,
    PrimaryButton,
    Dialog,
    DialogType,
    DialogFooter,
    DefaultButton,
    DetailsListLayoutMode,
    DetailsList,
    SelectionMode,
    Selection,
    Spinner,
} from '@fluentui/react';
import { TimeUser } from '../api/generated/data-contracts';
import { useMutation } from '@tanstack/react-query';
import { ApiCalls } from '../api/api';
import { useConfigValuesState } from '../hooks/useConfigValue';
import { useTranslation } from 'react-i18next';

function DelegateArea() {
    const { t } = useTranslation('common');
    const [dialogVisible, setDialogVisible] = useState<boolean>(false);
    const selectedUsers = useRef(
        new Selection({
            onSelectionChanged: () => {
                selectedPersonChange();
            },
        }),
    ).current;
    // const uiCtx = useUiContext();
    const {
        initialData: {
            // configuration: { workDayBoundaries: workDayBarActive, dateFormat },
            aggregatedUser: { originalUser, user, delegatedUsers },
        },
    } = useConfigValuesState();
    const [signInBtnText, setSignInBtnText] = useState<string>(() => t('delegateArea.signInAsMe', { value: originalUser.name }));
    const delegateUsers = useMemo<TimeUser[]>(
        () =>
            [...delegatedUsers, originalUser]
                .map(u => {
                    return { ...u, activated: false };
                })
                .sort((u1, u2) => {
                    // TODO FIND OUT WHAT THIS MEANS? cannot find "originaluser" anywhere
                    // if (u1.originaluser == true) {
                    //     return -1;
                    // }
                    return u1.name >= u2.name ? 1 : -1;
                }),
        [originalUser, delegatedUsers],
    );

    const delegatePossible = delegateUsers.length > 1;

    // useEffect(() => {
    //     let delegationUsers = [];

    //     const originalUser = uiCtx.user.originalUser;

    //     delegationUsers = [...uiCtx.user.delegatedUsers, originalUser].map((u: any) => {
    //         return { ...u, activated: false };
    //     });
    //     console.debug('delgusers', delegationUsers);
    //     setDelegatePossible(true);
    //     if (delegationUsers.length > 1) {
    //         setDelegatePossible(true);
    //     } else {
    //         setDelegatePossible(false);
    //     }
    //     setDelegateUsers(
    //         delegationUsers.sort((u1, u2) => {
    //             if (u1.originaluser == true) {
    //                 return -1;
    //             }
    //             return u1.name >= u2.name ? 1 : -1;
    //         }),
    //     );
    // }, []);
    const delegateChangeClick = () => {
        setDialogVisible(true);
    };
    const delegateChangeCancekClick = () => {
        setDialogVisible(false);
    };
    const { mutate: activateDelegate, isLoading } = useMutation((delegateId: string) => ApiCalls.activate({ delegateId }), {
        onSuccess: () => window.location.reload(),
    });
    const delegateAsPersonClick = () => {
        const selection = selectedUsers.getSelection() as TimeUser[];
        if (selection.length <= 0) {
            activateDelegate('00000000-0000-0000-0000-000000000000');
        } else {
            const selectedUser = selection[0];
            if (selectedUser.principalName === undefined) {
                // uiCtx.setTimeError(new TimeWarning('Not an actual user', 'This person does not exists as a user in your orginization and cannot be delegated'));
                throw new Error('Not an actual user. This person does not exists as a user in your orginization and cannot be delegated');
            }
            if (originalUser?.id === selectedUser.id) {
                activateDelegate('00000000-0000-0000-0000-000000000000');
            } else {
                activateDelegate(selectedUser.id);
            }
        }
    };
    const selectedPersonChange = (): void => {
        const selection = selectedUsers.getSelection() as TimeUser[];
        // console.log('selection', selection, uiCtx.user.originalUser);
        if (selection.length >= 1) {
            const selectedUser = selection[0];
            const text =
                selectedUser.id === user.id
                    ? t('delegateArea.signInAsMe', { value: selectedUser.name })
                    : t('delegateArea.signInAs', { value: selectedUser.name });
            setSignInBtnText(text);
        } else {
            const text = t('delegateArea.signInAsMe', { value: originalUser.name });
            setSignInBtnText(text);
        }
    };

    return (
        <>
            {delegatePossible ? (
                <div className="DelegateArea">
                    <PrimaryButton text={t('delegateArea.changeUser', { value: user.name })} onClick={delegateChangeClick} />
                </div>
            ) : (
                <></>
            )}
            <Dialog
                styles={{
                    main: [
                        {
                            selectors: {
                                '@media (min-width: 480px)': {
                                    minWidth: '800px',
                                },
                            },
                        },
                    ],
                }}
                hidden={!dialogVisible}
                onDismiss={delegateChangeCancekClick}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: t('delegateArea.signInAsDelegate.title'),
                    closeButtonAriaLabel: t('delegateArea.signInAsDelegate.close'),
                    subText: t('delegateArea.signInAsDelegate.subText'),
                }}
            >
                {isLoading ? (
                    <Stack className="logindialog" horizontalAlign="center" verticalAlign="center">
                        <Spinner label={t('delegateArea.loading')} />
                    </Stack>
                ) : (
                    <>
                        <DetailsList
                            items={delegateUsers}
                            columns={[{ key: 'name', name: t('delegateArea.table.name'), fieldName: 'name', minWidth: 300, maxWidth: 3000, isResizable: true }]}
                            selectionMode={SelectionMode.single}
                            setKey="set"
                            layoutMode={DetailsListLayoutMode.justified}
                            selection={selectedUsers}
                            selectionPreservedOnEmptyClick={true}
                            ariaLabelForSelectionColumn="Toggle selection"
                            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                            checkButtonAriaLabel="Row checkbox"
                            onActiveItemChanged={selectedPersonChange}
                            onItemInvoked={delegateAsPersonClick}
                        />
                        <DialogFooter>
                            <DefaultButton onClick={delegateChangeCancekClick} text={t('delegateArea.cancel')} />
                            <PrimaryButton onClick={delegateAsPersonClick} text={signInBtnText} />
                        </DialogFooter>
                    </>
                )}
            </Dialog>
        </>
    );
}

export default DelegateArea;
